import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { AppLayout } from "@/components/intel/Layout";
import { SeverityBadge } from "@/components/intel/SeverityBadge";
import { exportCaseDossier } from "@/lib/intel/pdf";
import { useInvestigations } from "@/lib/intel/store";
import { Download, FileText, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/reports")({
  head: () => ({ meta: [{ title: "Reports · CARIO INTEL" }] }),
  component: ReportsPage,
});

function ReportsPage() {
  const { state, logExport, verifyChain } = useInvestigations();
  const [busy, setBusy] = useState(false);
  const active = state.cases[0];
  const evidence = active ? state.evidence.filter((e) => e.caseId === active.id) : [];
  const notes = active ? state.notes.filter((n) => n.caseId === active.id) : [];
  const chain = active ? verifyChain(active.id) : { valid: false };
  const exportPdf = async () => {
    if (!active) return;
    setBusy(true);
    const custody = state.custody.filter((c) => c.caseId === active.id);
    const ok = await exportCaseDossier({
      caseRow: active, evidence, notes, custody, chain,
      filename: `${active.id}-cario-intel-dossier.pdf`,
    });
    if (ok) logExport(active.id, `Signed PDF dossier exported from reports workspace · ${evidence.length} evidence · ${notes.length} notes`);
    else alert("PDF export failed — see console for details.");
    setBusy(false);
  };

  return (
    <AppLayout>
    <ModuleShell
      eyebrow="Reports"
      title="Dossier generation"
      description="Export a source-cited intelligence report with evidence inventory, analyst notes, and chain-of-custody status."
      meta={<button onClick={exportPdf} disabled={!active || busy} className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50 ring-glow"><Download className="h-4 w-4" />{busy ? "Rendering…" : "Export PDF"}</button>}
    >
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Evidence items" value={evidence.length} tone="amber" />
        <StatTile label="Analyst notes" value={notes.length} />
        <StatTile label="Cases" value={state.cases.length} tone="success" />
        <StatTile label="Custody" value={chain.valid ? "VERIFIED" : "BROKEN"} tone={chain.valid ? "success" : "danger"} />
      </div>
      <article id="cario-report-export" className="glass rounded-xl p-6">
        <div className="flex flex-wrap items-start gap-4 border-b border-panel-border pb-5">
          <div className="grid h-12 w-12 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30"><FileText className="h-6 w-6 text-cyan" /></div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">CARIO INTEL · SIGNED DOSSIER</div>
            <h1 className="mt-1 font-display text-3xl">{active?.title ?? "No active case"}</h1>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{active?.id ?? "—"} · target {active?.target ?? "—"} · classification {active?.classification ?? "—"}</p>
          </div>
          <div className="rounded-md bg-success/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-success ring-1 ring-success/30"><ShieldCheck className="mr-1 inline h-3.5 w-3.5" />Chain {chain.valid ? "verified" : "unverified"}</div>
        </div>
        <section className="mt-6">
          <h2 className="font-display text-xl">Executive assessment</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {active
              ? (active.summary || "No executive assessment yet. Run a scan and add analyst notes to populate this dossier.")
              : "No active case. Run a scan from the command dashboard to open an investigation."}
          </p>
        </section>
        <section className="mt-6 grid gap-4 md:grid-cols-2">
          <div>
            <h2 className="font-display text-xl">Evidence inventory</h2>
            <div className="mt-2 space-y-2">
              {evidence.length === 0 && <div className="text-xs text-muted-foreground">No evidence yet.</div>}
              {evidence.map((e) => (
                <div key={e.id} className="rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                  <span className="font-mono text-cyan">{e.id}</span> · {e.label}
                  <div className="font-mono text-[10px] text-muted-foreground">{e.source} · {e.hash}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl">Custody trail</h2>
            <div className="mt-2 space-y-2">
              {state.custody.filter((c) => active && c.caseId === active.id).length === 0 && (
                <div className="text-xs text-muted-foreground">No custody events yet.</div>
              )}
              {state.custody.filter((c) => active && c.caseId === active.id).map((c) => (
                <div key={c.id} className="rounded-md border border-panel-border bg-background/40 p-2 text-xs">
                  <span className="font-mono text-cyan">{c.ts.slice(0, 19).replace("T", " ")}</span> · {c.action.replace(/_/g, " ")}
                  <div className="font-mono text-[10px] text-muted-foreground">{c.actor}{c.detail ? ` · ${c.detail}` : ""}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </article>
    </ModuleShell>
    </AppLayout>
  );
}